/**
 * Custom product card: interactions
 *
 * - One set of delegated listeners on `document` → works for cards rendered later by AJAX
 *   (product recommendations, search, collection filters, collection tabs, sliders).
 * - Guarded against double loading, so it is safe if the script tag appears more than once.
 * - Lazy-loads Dawn's own product-form.js / quick-add.js / quick-add.css only when a card is used,
 *   and only if the page has not loaded them already. No third-party libraries.
 */
(() => {
  if (window.customProductCard) return;

  const root = document.currentScript?.src ? document.currentScript.src.replace(/custom-product-card\.js.*$/, '') : null;
  const loaded = {};

  const assetUrl = (file) => {
    // Re-use an existing tag's URL (keeps Shopify's versioned CDN url) when available.
    const existing =
      document.querySelector(`script[src*="/${file}"]`) || document.querySelector(`link[href*="/${file}"]`);
    if (existing) return existing.src || existing.href;
    return root ? root + file : null;
  };

  const loadScript = (file, isDefined) => {
    if (isDefined()) return Promise.resolve();
    if (loaded[file]) return loaded[file];
    loaded[file] = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src*="/${file}"]`);
      if (existing) {
        // Script tag exists (deferred); wait until the element is defined.
        const check = () => (isDefined() ? resolve() : window.setTimeout(check, 50));
        return check();
      }
      const url = assetUrl(file);
      if (!url) return reject(new Error(`Missing ${file}`));
      const script = document.createElement('script');
      script.src = url;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return loaded[file];
  };

  const loadStyle = (file) => {
    if (document.querySelector(`link[href*="/${file}"]`)) return;
    const url = assetUrl(file);
    if (!url) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  };

  const ensureProductForm = () => loadScript('product-form.js', () => !!customElements.get('product-form'));
  const ensureQuickAdd = () => {
    loadStyle('quick-add.css');
    return Promise.all([
      ensureProductForm(),
      loadScript('quick-add.js', () => !!customElements.get('quick-add-modal')),
    ]);
  };

  // Warm up: load product-form.js as soon as the customer shows intent on any card.
  const warmUp = (event) => {
    const card = event.target.closest?.('.custom-product-card');
    if (!card) return;
    if (card.querySelector('product-form')) ensureProductForm();
    if (card.querySelector('[data-cpc-quick-add]')) ensureQuickAdd();
  };
  document.addEventListener('pointerover', warmUp, { passive: true });
  document.addEventListener('focusin', warmUp);
  document.addEventListener('touchstart', warmUp, { passive: true });

  // "Choose options" → open Dawn's quick-add modal for this card.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.custom-product-card [data-cpc-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    const selector = button.getAttribute('data-cpc-quick-add');

    ensureQuickAdd()
      .then(() => {
        // Dawn's modal-dialog moves itself to <body>, so look it up in the whole document.
        // quick-add.js shows the button spinner while it fetches the product.
        const modal = document.querySelector(selector);
        if (modal && typeof modal.show === 'function') {
          // Make sure the fixed-position modal is not trapped inside a transformed slider.
          if (modal.parentElement !== document.body) document.body.appendChild(modal);
          modal.show(button);
        } else {
          window.location.href = button.dataset.productUrl;
        }
      })
      .catch(() => {
        window.location.href = button.dataset.productUrl;
      });
  });

  // Direct add to cart: if product-form.js is not ready yet, hold the submit until it is,
  // instead of letting the browser post the form and leave the page.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('.custom-product-card')) return;
      if (customElements.get('product-form')) return; // Dawn's product-form handles it

      event.preventDefault();
      event.stopImmediatePropagation();
      ensureProductForm()
        .then(() => {
          // Element upgrades on define; its own submit listener now handles the request.
          window.requestAnimationFrame(() => form.requestSubmit ? form.requestSubmit() : form.submit());
        })
        .catch(() => form.submit());
    },
    true
  );

  window.customProductCard = { ensureProductForm, ensureQuickAdd };
})();
